import { ChatAdapterExtras, StreamingAdapterObserver } from '@nlux/core';
import { NluxUsageError } from '../types/error';
import { ChatAdapterOptions } from '../types/adapterOptions';
import { LangGraphAbstractAdapter } from './adapter';
import { parseStreamedEvent } from '../parser/parseStreamedEvent';
import { warn } from '../utils/warn';
import { adapterErrorToExceptionId } from '../utils/adapterErrorToExceptionId';

// Update your event type definition to include "updates" and "messages/partial"
type EventType =
  | 'messages'
  | 'messages/partial'
  | 'end'
  | 'metadata'
  | 'values'
  | 'heartbeat'
  | 'updates'
  | 'custom'; // Add 'custom' to the event types

export class LangGraphStreamAdapter<AiMsg> extends LangGraphAbstractAdapter<AiMsg> {
  constructor(options: ChatAdapterOptions<AiMsg>) {
    super(options);
  }

  async batchText(
    message: string,
    extras: ChatAdapterExtras<AiMsg>
  ): Promise<string | object | undefined> {
    throw new NluxUsageError({
      source: this.constructor.name,
      message: 'Cannot fetch text using the stream adapter!',
    });
  }

  async streamText(
    message: string,
    observer: StreamingAdapterObserver<string | object | undefined>,
    extras: ChatAdapterExtras<AiMsg>
  ): Promise<void> {
    const streamId = Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    // console.log('🆕 NEW STREAM CREATED');
    // console.log('🔑 Stream ID:', streamId);
    // console.log('💬 Message:', message);

    const body = this.getRequestBody(message, this.config, extras.conversationHistory);

    let lastAiMessageContent = '';
    let accumulatedContent = ''; // Track all content sent to user
    let streamCompleted = false;
    let lastProcessedMessageIndex = 0;

    // console.log(`[${streamId}] 🚀 Stream adapter starting request`);

    try {
      const res = await fetch(this.endpointUrl, {
        method: 'POST',
        headers: {
          ...this.headers,
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({
          ...JSON.parse(body),
          stream_mode: ['updates', 'custom'],
        }),
      });

      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const textDecoder = new TextDecoder();
      let chunkCount = 0;

      while (true) {
        const { value, done } = await reader.read();

        if (done) {
          // console.log(`[${streamId}] ✅ Stream reader done after`, chunkCount, 'chunks');
          // Only complete if we haven't received a proper 'end' event
          if (!streamCompleted) {
            // console.log(`[${streamId}] ⚠️ Stream reader finished but no 'end' event received`);
            setTimeout(() => {
              if (!streamCompleted) {
                // console.log(`[${streamId}] 📞 Force completing stream after reader done`);
                observer.complete();
                streamCompleted = true;
              }
            }, 500); // Longer delay as fallback
          }
          break;
        }

        chunkCount++;
        const rawChunk = textDecoder.decode(value);

        // Enhanced raw data logging
        // console.log(`[${streamId}] 📦 Chunk ${chunkCount} RAW DATA (${rawChunk.length} bytes):`);
        // console.log(`[${streamId}] 📦 Raw chunk content:`, JSON.stringify(rawChunk));
        // console.log(
        //   `[${streamId}] 📦 Raw chunk preview:`,
        //   rawChunk.substring(0, 200) + (rawChunk.length > 200 ? '...' : '')
        // );

        const eventData = parseStreamedEvent(rawChunk);

        // Enhanced event parsing logging
        // console.log(`[${streamId}] 🔍 Chunk ${chunkCount} PARSED EVENT:`);
        // console.log(`[${streamId}] 🔍 Event type:`, eventData?.event || 'UNDEFINED');
        // console.log(`[${streamId}] 🔍 Event data type:`, typeof eventData?.data);
        // console.log(`[${streamId}] 🔍 Event data:`, eventData?.data);
        // console.log(`[${streamId}] 🔍 Full eventData object:`, JSON.stringify(eventData, null, 2));

        if (eventData && !streamCompleted) {
          // Handle 'custom' events - these are direct progress messages
          if (eventData.event === 'custom' && eventData.data) {
            // console.log(`[${streamId}] 🎯 CUSTOM EVENT DETECTED`);
            // console.log(`[${streamId}] 🎯 Custom data:`, JSON.stringify(eventData.data, null, 2));

            if (
              eventData.data &&
              typeof eventData.data === 'object' &&
              'status' in eventData.data &&
              'message' in eventData.data
            ) {
              const customData = eventData.data as any;

              // console.log(`[${streamId}] 🎯 CUSTOM PROGRESS MESSAGE`);
              // console.log(`[${streamId}] 🎯 Status: ${customData.status}`);
              // console.log(`[${streamId}] 🎯 Message: ${customData.message}`);

              // Don't add extra formatting - the message already has emojis and formatting
              let progressDisplay = customData.message;

              // For error/warning status, append the error details if present
              if (
                (customData.status === 'error' || customData.status === 'warning') &&
                customData.error
              ) {
                progressDisplay += `\n> ${customData.error}`;
              }

              // Send the progress message to the UI
              // Add a newline for better spacing in the chat
              observer.next(progressDisplay + '\n\n');

              // Track the content
              accumulatedContent += progressDisplay + '\n\n';

              // For complete status, you might want to trigger completion
              if (customData.status === 'complete') {
                // console.log(`[${streamId}] 🎉 Workflow marked as complete`);
              }
            }
          }
          // Handle 'updates' events - this is where your progress messages come from
          else if (eventData.event === 'updates' && eventData.data) {
            // console.log(`[${streamId}] 🔄 UPDATES EVENT DETECTED`);
            // console.log(`[${streamId}] 🔄 Updates data:`, JSON.stringify(eventData.data, null, 2));

            // Check for custom progress messages from tools
            // These come through as updates with a specific structure
            if (eventData.data && typeof eventData.data === 'object') {
              // Check if this is a direct custom event (from 'event: custom')
              // These have status and message at the top level
              if ('status' in eventData.data && 'message' in eventData.data) {
                const customData = eventData.data as any;

                // console.log(`[${streamId}] 🎯 CUSTOM PROGRESS MESSAGE (direct)`);
                // console.log(`[${streamId}] 🎯 Status: ${customData.status}`);
                // console.log(`[${streamId}] 🎯 Message: ${customData.message}`);

                // Don't add extra formatting - the message already has emojis and formatting
                let progressDisplay = customData.message;

                // For error/warning status, append the error details if present
                if (
                  (customData.status === 'error' || customData.status === 'warning') &&
                  customData.error
                ) {
                  progressDisplay += `\n> ${customData.error}`;
                }

                // Send the progress message to the UI
                // Add a newline for better spacing in the chat
                observer.next(progressDisplay + '\n\n');

                // Track the content
                accumulatedContent += progressDisplay + '\n\n';

                // For complete status, you might want to trigger completion
                if (customData.status === 'complete') {
                  // console.log(`[${streamId}] 🎉 Workflow marked as complete`);
                }
              }
              // Otherwise check if updates are wrapped in node names
              else {
                // The updates come wrapped in the node name (e.g., "repeat_orders_for_county")
                const nodeNames = Object.keys(eventData.data);

                for (const nodeName of nodeNames) {
                  const nodeData = eventData.data[nodeName];

                  // Check if this is a custom progress message from get_stream_writer()
                  if (
                    nodeData &&
                    typeof nodeData === 'object' &&
                    'status' in nodeData &&
                    'message' in nodeData
                  ) {
                    // console.log(`[${streamId}] 📊 PROGRESS MESSAGE from ${nodeName}`);
                    // console.log(`[${streamId}] 📊 Status: ${nodeData.status}`);
                    // console.log(`[${streamId}] 📊 Message: ${nodeData.message}`);

                    // Don't add extra formatting - the message already has emojis and formatting
                    let progressDisplay = nodeData.message;

                    // For error/warning status, append the error details if present
                    if (
                      (nodeData.status === 'error' || nodeData.status === 'warning') &&
                      nodeData.error
                    ) {
                      progressDisplay += `\n> ${nodeData.error}`;
                    }

                    // Send the progress message to the UI
                    // Add a newline for better spacing in the chat
                    observer.next(progressDisplay + '\n\n');

                    // Track the content
                    accumulatedContent += progressDisplay + '\n\n';

                    // For complete status, you might want to trigger completion
                    if (nodeData.status === 'complete') {
                      // console.log(`[${streamId}] 🎉 Workflow marked as complete`);
                    }
                  }
                }
              }
            }

            // Check for call_model updates that contain progress messages
            if (
              eventData.data &&
              typeof eventData.data === 'object' &&
              'call_model' in eventData.data
            ) {
              // console.log(`[${streamId}] 🤖 CALL_MODEL UPDATE DETECTED`);
              const callModelData = (eventData.data as any).call_model;

              if (
                callModelData &&
                callModelData.messages &&
                Array.isArray(callModelData.messages)
              ) {
                // console.log(`[${streamId}] 🤖 Processing call_model messages`);

                const aiMessages = callModelData.messages.filter((msg: any) => msg.type === 'ai');

                for (const aiMessage of aiMessages) {
                  const currentContent = aiMessage.content || '';
                  const isProgress = aiMessage.additional_kwargs?.is_progress;

                  // console.log(`[${streamId}] 🤖 AI Message - Content:`, currentContent);
                  // console.log(`[${streamId}] 🤖 AI Message - Is Progress:`, isProgress);

                  if (currentContent && currentContent !== lastAiMessageContent) {
                    lastAiMessageContent = currentContent;

                    const processedContent = this.preProcessAiStreamedChunk(currentContent, extras);

                    if (processedContent !== undefined) {
                      // console.log(`[${streamId}] 📝 Sending content from call_model update`);

                      // For progress messages, add them immediately
                      // For regular messages, accumulate them
                      if (isProgress) {
                        observer.next(processedContent as string | object);
                        observer.next('\n\n' as string); // Add spacing after progress
                      } else {
                        const contentWithBreak =
                          typeof processedContent === 'string'
                            ? `${processedContent}\n\n`
                            : processedContent;
                        observer.next(contentWithBreak as string | object);
                      }

                      accumulatedContent += currentContent + '\n\n';
                    }
                  }
                }
              }
            }

            // Check if this is an end_conversation update
            else if (
              eventData.data &&
              typeof eventData.data === 'object' &&
              'end_conversation' in eventData.data
            ) {
              // console.log(`[${streamId}] 🏁 END_CONVERSATION UPDATE DETECTED`);
              const endConversationData = (eventData.data as any).end_conversation;

              if (
                endConversationData &&
                endConversationData.messages &&
                Array.isArray(endConversationData.messages)
              ) {
                // console.log(`[${streamId}] 🏁 Processing end_conversation messages`);

                // Process any final content
                const aiMessages = endConversationData.messages.filter(
                  (msg: any) => msg.type === 'ai' && msg.content
                );
                if (aiMessages.length > 0) {
                  const latestAiMessage = aiMessages[aiMessages.length - 1];
                  const currentContent = latestAiMessage.content || '';

                  if (currentContent && currentContent !== lastAiMessageContent) {
                    lastAiMessageContent = currentContent;

                    const processedContent = this.preProcessAiStreamedChunk(currentContent, extras);

                    if (processedContent !== undefined) {
                      // console.log(`[${streamId}] 📝 Sending final content from end_conversation`);

                      const contentWithBreak =
                        typeof processedContent === 'string'
                          ? `${processedContent}\n\n`
                          : processedContent;
                      observer.next(contentWithBreak as string | object);
                    }
                  }
                }

                // Look for end signal
                const endMessage = endConversationData.messages.find(
                  (msg: any) =>
                    msg.type === 'ai' &&
                    msg.additional_kwargs?.event_type === 'end' &&
                    msg.additional_kwargs?.stream_end === true
                );

                if (endMessage) {
                  // console.log(`[${streamId}] 🏁 End signal found - completing stream`);
                  if (!streamCompleted) {
                    observer.complete();
                    streamCompleted = true;
                  }
                  return;
                }
              }
            }
          }

          // Keep all your existing event handlers for backward compatibility
          // Handle 'messages' events
          if (eventData.event === 'messages' && eventData.data) {
            // console.log(`[${streamId}] 📨 MESSAGES EVENT DETECTED`);
            const messages = Array.isArray(eventData.data) ? eventData.data : [eventData.data];
            // console.log(`[${streamId}] 📨 Messages array length:`, messages.length);
            // console.log(`[${streamId}] 📨 All messages:`, JSON.stringify(messages, null, 2));

            // Process AI messages
            const aiMessages = messages.filter((msg) => msg.type === 'ai');
            // console.log(`[${streamId}] 📨 AI messages found:`, aiMessages.length);

            if (aiMessages.length > 0) {
              const latestAiMessage = aiMessages[aiMessages.length - 1];
              const currentContent = latestAiMessage.content || '';

              // console.log(
              //   `[${streamId}] 📨 Latest AI message:`,
              //   JSON.stringify(latestAiMessage, null, 2)
              // );
              // console.log(`[${streamId}] 📨 Current content length:`, currentContent.length);
              // console.log(
              //   `[${streamId}] 📨 Current content preview:`,
              //   currentContent.substring(0, 100) + (currentContent.length > 100 ? '...' : '')
              // );

              // console.log(
              //   `[${streamId}] 🔄 Processing message content (${currentContent.length} chars)`
              // );

              // Check if this content is different from the last processed content
              if (currentContent !== lastAiMessageContent) {
                // console.log(`[${streamId}] 📨 Content is NEW - processing...`);
                lastAiMessageContent = currentContent;

                const processedContent = this.preProcessAiStreamedChunk(currentContent, extras);

                if (processedContent !== undefined) {
                  // console.log(`[${streamId}] 📝 Sending processed content from messages event`);

                  const processedContentWithParagraphBreak =
                    typeof processedContent === 'string'
                      ? `${processedContent}\n\n`
                      : processedContent;

                  observer.next(processedContentWithParagraphBreak as string | object);
                } else {
                  // console.log(`[${streamId}] 📨 Processed content is undefined - skipping`);
                }
              } else {
                // console.log(`[${streamId}] 📨 Content is DUPLICATE - skipping`);
              }
            } else {
              // console.log(`[${streamId}] 📨 No AI messages found in this event`);
            }
          }
          // Handle 'messages/partial' events - for partial/streaming message content
          else if (eventData.event === 'messages/partial' && eventData.data) {
            // console.log(`[${streamId}] 📨🔄 MESSAGES/PARTIAL EVENT DETECTED`);
            const messages = Array.isArray(eventData.data) ? eventData.data : [eventData.data];
            // console.log(`[${streamId}] 📨🔄 Partial messages array length:`, messages.length);
            // console.log(
            //   `[${streamId}] 📨🔄 All partial messages:`,
            //   JSON.stringify(messages, null, 2)
            // );

            // Process AI messages
            const aiMessages = messages.filter((msg) => msg.type === 'ai');
            // console.log(`[${streamId}] 📨🔄 Partial AI messages found:`, aiMessages.length);

            if (aiMessages.length > 0) {
              const latestAiMessage = aiMessages[aiMessages.length - 1];
              const currentContent = latestAiMessage.content || '';

              // console.log(
              //   `[${streamId}] 📨🔄 Latest partial AI message:`,
              //   JSON.stringify(latestAiMessage, null, 2)
              // );
              // console.log(
              //   `[${streamId}] 📨🔄 Current partial content length:`,
              //   currentContent.length
              // );
              // console.log(
              //   `[${streamId}] 📨🔄 Current partial content preview:`,
              //   currentContent.substring(0, 100) + (currentContent.length > 100 ? '...' : '')
              // );

              // console.log(
              //   `[${streamId}] 🔄 Processing partial message content (${currentContent.length} chars)`
              // );

              // For partial messages, always process (they're incremental)
              // console.log(`[${streamId}] 📨🔄 Processing partial content...`);
              lastAiMessageContent = currentContent;

              const processedContent = this.preProcessAiStreamedChunk(currentContent, extras);

              if (processedContent !== undefined) {
                // console.log(
                //   `[${streamId}] 📝 Sending processed content from messages/partial event`
                // );

                // Don't add paragraph break for partial messages - they're incremental
                observer.next(processedContent as string | object);
              } else {
                // console.log(`[${streamId}] 📨🔄 Processed partial content is undefined - skipping`);
              }
            } else {
              // console.log(`[${streamId}] 📨🔄 No AI messages found in this partial event`);
            }
          }
          // Handle 'updates' events
          else if (eventData.event === 'updates' && eventData.data) {
            // console.log(`[${streamId}] 🔄 UPDATES EVENT DETECTED`);
            // console.log(`[${streamId}] 🔄 Updates data:`, JSON.stringify(eventData.data, null, 2));

            // Check for custom progress messages from tools
            // These come through as updates with a specific structure
            if (eventData.data && typeof eventData.data === 'object') {
              // Check if this is a direct custom event (from 'event: custom')
              // These have status and message at the top level
              if ('status' in eventData.data && 'message' in eventData.data) {
                const customData = eventData.data as any;

                // console.log(`[${streamId}] 🎯 CUSTOM PROGRESS MESSAGE (direct)`);
                // console.log(`[${streamId}] 🎯 Status: ${customData.status}`);
                // console.log(`[${streamId}] 🎯 Message: ${customData.message}`);

                // Don't add extra formatting - the message already has emojis and formatting
                let progressDisplay = customData.message;

                // For error/warning status, append the error details if present
                if (
                  (customData.status === 'error' || customData.status === 'warning') &&
                  customData.error
                ) {
                  progressDisplay += `\n> ${customData.error}`;
                }

                // Send the progress message to the UI
                // Add a newline for better spacing in the chat
                observer.next(progressDisplay + '\n\n');

                // Track the content
                accumulatedContent += progressDisplay + '\n\n';

                // For complete status, you might want to trigger completion
                if (customData.status === 'complete') {
                  // console.log(`[${streamId}] 🎉 Workflow marked as complete`);
                }
              }
              // Otherwise check if updates are wrapped in node names
              else {
                // The updates come wrapped in the node name (e.g., "repeat_orders_for_county")
                const nodeNames = Object.keys(eventData.data);

                for (const nodeName of nodeNames) {
                  const nodeData = eventData.data[nodeName];

                  // Check if this is a custom progress message from get_stream_writer()
                  if (
                    nodeData &&
                    typeof nodeData === 'object' &&
                    'status' in nodeData &&
                    'message' in nodeData
                  ) {
                    // console.log(`[${streamId}] 📊 PROGRESS MESSAGE from ${nodeName}`);
                    // console.log(`[${streamId}] 📊 Status: ${nodeData.status}`);
                    // console.log(`[${streamId}] 📊 Message: ${nodeData.message}`);

                    // Don't add extra formatting - the message already has emojis and formatting
                    let progressDisplay = nodeData.message;

                    // For error/warning status, append the error details if present
                    if (
                      (nodeData.status === 'error' || nodeData.status === 'warning') &&
                      nodeData.error
                    ) {
                      progressDisplay += `\n> ${nodeData.error}`;
                    }

                    // Send the progress message to the UI
                    // Add a newline for better spacing in the chat
                    observer.next(progressDisplay + '\n\n');

                    // Track the content
                    accumulatedContent += progressDisplay + '\n\n';

                    // For complete status, you might want to trigger completion
                    if (nodeData.status === 'complete') {
                      // console.log(`[${streamId}] 🎉 Workflow marked as complete`);
                    }
                  }
                }
              }
            }
            // Handle 'values' events (legacy support)
          } else if (eventData.event === 'values' && eventData.data) {
            // console.log(`[${streamId}] 📊 VALUES EVENT DETECTED`);
            const data = eventData.data as { messages: any[]; scores: any[] };
            // console.log(`[${streamId}] 📊 Values data:`, JSON.stringify(data, null, 2));

            if (data.messages && Array.isArray(data.messages)) {
              // console.log(`[${streamId}] 📊 Values messages count:`, data.messages.length);

              // Find the index of the last human message
              const lastHumanMessageIndex = data.messages.findLastIndex(
                (msg) => msg.type === 'human'
              );
              // console.log(`[${streamId}] 📊 Last human message index:`, lastHumanMessageIndex);

              // Only process AI messages that come after the last human message
              const messagesAfterLastHuman =
                lastHumanMessageIndex >= 0
                  ? data.messages.slice(lastHumanMessageIndex + 1)
                  : data.messages;
              // console.log(
              //   `[${streamId}] 📊 Messages after last human:`,
              //   messagesAfterLastHuman.length
              // );

              // Check for end event only in messages after last human message
              const endMessage = messagesAfterLastHuman.find(
                (msg) =>
                  msg.type === 'ai' &&
                  msg.additional_kwargs?.event_type === 'end' &&
                  msg.additional_kwargs?.stream_end === true
              );

              if (endMessage) {
                // console.log(`[${streamId}] 🏁 End event detected in VALUES - completing stream`);
                // console.log(`[${streamId}] 🏁 End message:`, JSON.stringify(endMessage, null, 2));
                if (!streamCompleted) {
                  // console.log(`[${streamId}] 📞 Calling observer.complete() from values end event`);
                  observer.complete();
                  streamCompleted = true;
                }
                return;
              }

              const aiMessagesAfterLastHuman = messagesAfterLastHuman.filter(
                (msg) => msg.type === 'ai'
              );
              // console.log(
              //   `[${streamId}] 📊 AI messages after last human:`,
              //   aiMessagesAfterLastHuman.length
              // );

              if (aiMessagesAfterLastHuman.length > 0) {
                const latestAiMessage =
                  aiMessagesAfterLastHuman[aiMessagesAfterLastHuman.length - 1];
                const currentContent = latestAiMessage.content || '';

                // console.log(
                //   `[${streamId}] 📊 Latest AI message from values:`,
                //   JSON.stringify(latestAiMessage, null, 2)
                // );
                // console.log(
                //   `[${streamId}] 🔄 Processing content with preProcessAiStreamedChunk (${currentContent.length} chars)`
                // );

                // Check if this content is different from the last processed content
                if (currentContent !== lastAiMessageContent) {
                  // console.log(`[${streamId}] 📊 VALUES content is NEW - processing...`);
                  lastAiMessageContent = currentContent;

                  // Use the inherited method instead of calling outputPreProcessor directly
                  const processedContent = this.preProcessAiStreamedChunk(currentContent, extras);

                  if (processedContent !== undefined) {
                    // console.log(
                    //   `[${streamId}] 📝 Sending processed content (${
                    //     typeof processedContent === 'string'
                    //       ? processedContent.length
                    //       : typeof processedContent
                    //   } chars)`
                    // );

                    // Add paragraph break after the processed content
                    const processedContentWithParagraphBreak =
                      typeof processedContent === 'string'
                        ? `${processedContent}\n\n`
                        : processedContent;

                    observer.next(processedContentWithParagraphBreak as string | object);
                  } else {
                    // console.log(
                    //   `[${streamId}] 📊 Processed content from values is undefined - skipping`
                    // );
                  }
                } else {
                  // console.log(`[${streamId}] ⏭️ Skipping duplicate content from values`);
                }
              }
            }
          } else if (eventData.event === 'end') {
            // console.log(`[${streamId}] 🏁 END EVENT DETECTED - completing stream immediately`);
            // console.log(
            //   `[${streamId}] 🏁 End event data:`,
            //   JSON.stringify(eventData.data, null, 2)
            // );
            if (!streamCompleted) {
              // console.log(`[${streamId}] 📞 Calling observer.complete() from end event`);
              observer.complete();
              streamCompleted = true;
            }
            // Don't break here - let the stream reader finish naturally
          } else if (eventData.event === 'metadata') {
            // console.log(`[${streamId}] 📋 METADATA EVENT DETECTED`);
            // console.log(`[${streamId}] 📋 Metadata:`, JSON.stringify(eventData.data, null, 2));
          } else if (eventData.event === 'heartbeat') {
            console.log(`[${streamId}] 💓 HEARTBEAT EVENT DETECTED`);
            // console.log(
            //   `[${streamId}] 💓 Heartbeat data:`,
            //   JSON.stringify(eventData.data, null, 2)
            // );
          } else {
            // console.log(`[${streamId}] ❓ UNKNOWN EVENT TYPE:`, eventData.event);
            // console.log(
            //   `[${streamId}] ❓ Unknown event data:`,
            //   JSON.stringify(eventData.data, null, 2)
            // );
          }
        } else if (streamCompleted) {
          // console.log(`[${streamId}] ⚠️ Received chunk after stream completed - ignoring`);
        } else if (!eventData) {
          // console.log(
          //   `[${streamId}] ⚠️ No eventData parsed from chunk - raw data might be malformed`
          // );
        }
      }
    } catch (error) {
      console.error(`[${streamId}] ❌ Stream error:`, error);
      if (!streamCompleted) {
        observer.error(
          new NluxUsageError({
            source: this.constructor.name,
            message: error.message,
          })
        );
        streamCompleted = true;
      }
    }
  }
}
